/**
 * Uniform multipart part chunker (P2 — F3).
 *
 * R2's multipart API — unlike S3's — REQUIRES every non-final part to be
 * byte-identical in size and rejects `complete()` otherwise. The old flush
 * ("emit a part once the buffer crosses PART_SIZE_BYTES, after appending a whole
 * row") produced parts of `PART_SIZE_BYTES + a variable per-row overshoot` (and
 * part 1 also carried the header), so a two-part upload passed while the
 * 500k-row flagship (~10 parts) failed at complete after minutes of work.
 *
 * This chunker decouples part boundaries from row boundaries: appended byte
 * chunks accumulate in a buffer, and full parts are SLICED at exactly
 * `partSizeBytes` regardless of where a row ends (a CSV row may straddle two
 * parts — R2 reassembles the byte stream verbatim, so the object is unchanged).
 * The header row is just the first appended bytes; it no longer makes part 1 a
 * different size.
 *
 * Invariant: every part from {@link PartChunker.drainFullParts} is EXACTLY
 * `partSizeBytes`; {@link PartChunker.drainRemainder} yields the single final
 * part of `1..partSizeBytes` bytes (or null when nothing remains).
 *
 * Pure byte bookkeeping — no I/O, no DOM — so it unit-tests directly and the
 * scrub pipeline stays the only place that talks to the transport.
 */
export class PartChunker {
  private readonly partSizeBytes: number;
  /** Appended chunks not yet sliced into parts, in order. */
  private buffered: Uint8Array[] = [];
  private bufferedBytes = 0;

  constructor(partSizeBytes: number) {
    if (!Number.isInteger(partSizeBytes) || partSizeBytes <= 0) {
      throw new Error(`PartChunker: invalid part size ${partSizeBytes}`);
    }
    this.partSizeBytes = partSizeBytes;
  }

  /** Bytes currently buffered (not yet drained). */
  get pendingBytes(): number {
    return this.bufferedBytes;
  }

  /** Append one chunk of bytes (any size, including empty). */
  append(bytes: Uint8Array): void {
    if (bytes.byteLength === 0) return;
    this.buffered.push(bytes);
    this.bufferedBytes += bytes.byteLength;
  }

  /**
   * Drain every FULL part currently available: zero or more `Uint8Array`s of
   * exactly `partSizeBytes`, leaving the remainder (< partSizeBytes) buffered.
   */
  drainFullParts(): Uint8Array[] {
    const parts: Uint8Array[] = [];
    while (this.bufferedBytes >= this.partSizeBytes) {
      parts.push(this.takeBytes(this.partSizeBytes));
    }
    return parts;
  }

  /**
   * Drain the final (short) part: whatever remains, as one `Uint8Array` of
   * `1..partSizeBytes` bytes — or null when nothing is buffered. Call after the
   * last append + a final {@link drainFullParts}.
   */
  drainRemainder(): Uint8Array | null {
    if (this.bufferedBytes === 0) return null;
    return this.takeBytes(this.bufferedBytes);
  }

  /** Remove exactly `n` buffered bytes (n ≤ bufferedBytes) as one contiguous array. */
  private takeBytes(n: number): Uint8Array {
    const out = new Uint8Array(n);
    let offset = 0;
    while (offset < n) {
      const head = this.buffered[0];
      if (head === undefined) {
        throw new Error('PartChunker: buffer accounting drifted (empty buffer mid-take)');
      }
      const take = Math.min(head.byteLength, n - offset);
      out.set(take === head.byteLength ? head : head.subarray(0, take), offset);
      offset += take;
      if (take === head.byteLength) {
        this.buffered.shift();
      } else {
        this.buffered[0] = head.subarray(take);
      }
    }
    this.bufferedBytes -= n;
    return out;
  }
}
